import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  BlogPostsFilterParams,
  BlogPostType,
  BlogPostsListResponse,
  CreateBlogPostParams,
  UpdateBlogPostParams
} from '@/types/blog-post'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum } from '@/enums'

// API functions
import {
  fetchBlogPosts,
  fetchBlogPost,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  bulkDeleteBlogPosts
} from '@/lib/api'

type BlogPostStateType = {
  data: BlogPostType | null
  isLoading: boolean
  error: string | null
}

type BlogPostsListStateType = {
  data: BlogPostsListResponse | null
  isLoading: boolean
  error: string | null
}

type BlogPostsStoreState = {
  blogPost: BlogPostStateType
  blogPostsList: BlogPostsListStateType
  filters: BlogPostsFilterParams
  selectedBlogPostIds: string[]
  setFilters: (filters: Partial<BlogPostsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchBlogPosts: (params?: BlogPostsFilterParams) => Promise<BlogPostsListResponse>
  resetBlogPostsList: () => void

  // Blog Post operations
  getBlogPost: (id: string) => Promise<BlogPostType>
  createBlogPost: (params: CreateBlogPostParams) => Promise<BlogPostType>
  updateBlogPost: (id: string, params: UpdateBlogPostParams) => Promise<BlogPostType>
  deleteBlogPost: (id: string) => Promise<void>
  bulkDeleteBlogPosts: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectBlogPost: (id: string) => void
  deselectBlogPost: (id: string) => void
  toggleBlogPostSelection: (id: string) => void
  selectAllBlogPosts: (blogPosts: BlogPostType[]) => void
  deselectAllBlogPosts: () => void
  isBlogPostSelected: (id: string) => boolean
  resetBlogPost: () => void
}

const defaultFilters: BlogPostsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'translations.en-US.title',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const useBlogPostsStore = create<BlogPostsStoreState>()(
  devtools((set, get) => ({
    blogPost: {
      data: null,
      isLoading: false,
      error: null
    },
    blogPostsList: {
      data: null,
      isLoading: false,
      error: null
    },
    selectedBlogPostIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof BlogPostsFilterParams]
          } else {
            updatedFilters[key as keyof BlogPostsFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: BlogPostsFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as BlogPostsFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof BlogPostsFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getBlogPost: async id => {
      set(state => ({
        blogPost: {
          ...state.blogPost,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchBlogPost(id)

        set(state => ({
          blogPost: {
            ...state.blogPost,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          blogPost: {
            ...state.blogPost,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createBlogPost: async params => {
      set(state => ({
        blogPost: {
          ...state.blogPost,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createBlogPost(params)

        set(state => ({
          blogPost: {
            ...state.blogPost,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          blogPost: {
            ...state.blogPost,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updateBlogPost: async (id, params) => {
      set(state => ({
        blogPost: {
          ...state.blogPost,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateBlogPost(id, params)

        set(state => ({
          blogPost: {
            ...state.blogPost,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          blogPost: {
            ...state.blogPost,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteBlogPost: async id => {
      set(state => ({
        blogPost: {
          ...state.blogPost,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteBlogPost(id)

        set(state => ({
          blogPost: {
            ...state.blogPost,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          blogPost: {
            ...state.blogPost,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetBlogPost: () => {
      set(state => ({
        blogPost: {
          ...state.blogPost,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectBlogPost: id => {
      set(state => {
        if (state.selectedBlogPostIds.includes(id)) {
          return state
        }
        return { selectedBlogPostIds: [...state.selectedBlogPostIds, id] }
      })
    },

    deselectBlogPost: id => {
      set(state => ({
        selectedBlogPostIds: state.selectedBlogPostIds.filter(blogPostId => blogPostId !== id)
      }))
    },

    toggleBlogPostSelection: id => {
      set(state => {
        if (state.selectedBlogPostIds.includes(id)) {
          return { selectedBlogPostIds: state.selectedBlogPostIds.filter(blogPostId => blogPostId !== id) }
        }
        return { selectedBlogPostIds: [...state.selectedBlogPostIds, id] }
      })
    },

    selectAllBlogPosts: blogPosts => {
      set(() => ({
        selectedBlogPostIds: blogPosts.map(blogPost => blogPost.id)
      }))
    },

    deselectAllBlogPosts: () => {
      set(() => ({ selectedBlogPostIds: [] }))
    },

    isBlogPostSelected: id => {
      return get().selectedBlogPostIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteBlogPosts: async ids => {
      set(state => ({
        blogPost: {
          ...state.blogPost,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteBlogPosts({ ids })

        set(state => ({
          blogPost: {
            ...state.blogPost,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedBlogPostIds: state.selectedBlogPostIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          blogPost: {
            ...state.blogPost,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchBlogPosts: async (params?: BlogPostsFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        blogPostsList: {
          ...state.blogPostsList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchBlogPosts(filtersToUse)

        set(state => ({
          blogPostsList: {
            ...state.blogPostsList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          blogPostsList: {
            ...state.blogPostsList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetBlogPostsList: () => {
      set(state => ({
        blogPostsList: {
          ...state.blogPostsList,
          data: null,
          error: null
        }
      }))
    }
  }))
)

export default useBlogPostsStore
